import React, { useEffect, useState } from 'react';
import Get from '../../../api/internal/Get';
import Select from '../../../assets/essentials/Select';
import OptionList from '../../../helpers/options/OptionList';
import Button from '../../../assets/essentials/Button';
import TextArea from '../../../assets/essentials/TextArea';
import PostBody from '../../../api/internal/PostBody';
import Close from '../../../helpers/modal/CloseModal';
import { ComchekIssueType, ComchekRequest } from '../../../interfaces/comchek';
import Input from '../../../assets/essentials/Input';

type ComchekCreateProps = {
  driverId?: number;
  loadId?: number;
  rentalId?: number;
  onSubmit?: () => void;
  onSuccess?: () => void;
};

export default function ComchekCreate({
  driverId,
  loadId,
  rentalId,
  onSubmit,
  onSuccess,
}: ComchekCreateProps) {
  const [issueTypeOptions, setIssueTypeOptions] = useState<ComchekIssueType[]>(
    [],
  );
  const [driverOptions, setDriverOptions] = useState<DriverOption[]>([]);
  const [assetOptions, setAssetOptions] = useState<AssetOption[]>([]);
  const [comchekType, setComchekType] = useState<
    'load' | 'rental' | 'weeklyRental' | ''
  >(loadId ? 'load' : rentalId ? 'rental' : '');
  const [loadOrRentalId, setLoadOrRentalId] = useState(
    loadId || rentalId || '',
  );
  const [selectedIssueTypeId, setSelectedIssueTypeId] = useState<number | null>(
    null,
  );
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    Get('/Comchek/GetComchekIssueTypes').then(res => {
      if (res) {
        setIssueTypeOptions(res.data);
      }
    });
    Get('/Employee/GetDriverOptions').then(res => {
      if (res) {
        setDriverOptions(res.data);
      }
    });
    Get('/Asset/GetAssetList').then(res => {
      if (res) {
        setAssetOptions(res.data);
      }
    });
  }, []);

  const truckOptions = assetOptions.filter(asset => asset.group === 'Truck');

  const trailerOptions = assetOptions.filter(
    asset => asset.group === 'Trailer',
  );

  const selectedIssueType = issueTypeOptions.find(
    type => type.comchekIssueTypeId === selectedIssueTypeId,
  );
  const isLumperIssueType =
    selectedIssueType?.reasonCode.toUpperCase() === 'LUMPER';

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsCreating(true);
    const formData = new FormData(e.currentTarget);
    const request: ComchekRequest = {
      amount: parseFloat(formData.get('amount') as string),
      driverId: parseInt(formData.get('driverId') as string),
      comchekIssueTypeId: parseInt(
        formData.get('comchekIssueTypeId') as string,
      ),
      loadId:
        comchekType === 'load' ? parseInt(loadOrRentalId as string) : undefined,
      rentalId:
        comchekType === 'rental'
          ? parseInt(loadOrRentalId as string)
          : undefined,
      truckId: parseInt(formData.get('truckId') as string),
      trailerId: parseInt(formData.get('trailerId') as string),
      note: (formData.get('note') as string) || null,
      isForWeeklyRental: comchekType === 'weeklyRental',
    };

    PostBody('/Comchek/CreateComchek', request)
      .then(res => {
        if (res) {
          Close();
          onSubmit();
          onSuccess();
        }
      })
      .finally(() => {
        setIsCreating(false);
      });
  };

  return (
    <form onSubmit={handleSubmit}>
      <label htmlFor="amount">Amount</label>
      <Input
        type="number"
        id="amount"
        name="amount"
        min="0"
        max="9999.99"
        step="0.01"
        placeholder="0.00"
        required
      />
      <>
        <label htmlFor="comchekIssueTypeId">Issue Reason</label>
        <Select
          required
          name="comchekIssueTypeId"
          id="comchekIssueTypeId"
          onChange={e =>
            setSelectedIssueTypeId(parseInt(e.target.value) || null)
          }
        >
          <option value="" hidden>
            -- Required --
          </option>
          <OptionList
            optionData={issueTypeOptions}
            attributeID="comchekIssueTypeId"
            attributeName="reason"
          />
        </Select>
      </>
      <>
        <label htmlFor="driverId">Driver</label>
        <Select defaultValue={driverId || ''} name="driverId" id="driverId">
          <option value="" hidden>
            -- N/A --
          </option>
          <OptionList
            optionData={driverOptions}
            attributeID="driverId"
            attributeName="name"
          />
        </Select>
      </>
      <>
        <fieldset>
          <legend>Load/Rental</legend>
          <div>
            <input
              type="radio"
              id="loadOrRental-load"
              name="loadOrRental"
              value="load"
              checked={comchekType === 'load'}
              onChange={() => {
                setComchekType('load');
                setLoadOrRentalId('');
              }}
            />
            <label htmlFor="loadOrRental-load">Load</label>
          </div>
          <div>
            <input
              type="radio"
              id="loadOrRental-rental"
              name="loadOrRental"
              value="rental"
              checked={comchekType === 'rental'}
              onChange={() => {
                setComchekType('rental');
                setLoadOrRentalId('');
              }}
            />
            <label htmlFor="loadOrRental-rental">Rental</label>
          </div>
          <div>
            <input
              type="radio"
              id="loadOrRental-weeklyRental"
              name="loadOrRental"
              value="weeklyRental"
              checked={comchekType === 'weeklyRental'}
              onChange={() => {
                setComchekType('weeklyRental');
                setLoadOrRentalId('');
              }}
            />
            <label htmlFor="loadOrRental-weeklyRental">Weekly Rental</label>
          </div>
          <div>
            <input
              type="radio"
              id="loadOrRental-none"
              name="loadOrRental"
              value=""
              checked={comchekType === ''}
              onChange={() => {
                setComchekType('');
                setLoadOrRentalId('');
              }}
            />
            <label htmlFor="loadOrRental-none">N/A</label>
          </div>
        </fieldset>
      </>
      <>
        <label htmlFor="loadOrRentalId">
          {comchekType === 'load'
            ? 'Load #'
            : comchekType === 'rental'
            ? 'Rental #'
            : 'N/A'}
        </label>
        <Input
          type="number"
          id="loadOrRentalId"
          name="loadOrRentalId"
          min="0"
          placeholder={
            comchekType === 'load'
              ? 'Enter load #'
              : comchekType === 'rental'
              ? 'Enter rental #'
              : 'Enter #'
          }
          value={loadOrRentalId}
          onChange={e => setLoadOrRentalId(e.target.value)}
          disabled={!comchekType || comchekType === 'weeklyRental'}
        />
      </>
      <>
        <label htmlFor="truckId">Truck</label>
        <Select name="truckId" id="truckId" disabled={isLumperIssueType}>
          <option value="">-- N/A --</option>
          <OptionList
            optionData={truckOptions}
            attributeID="truckId"
            attributeName="name"
          />
        </Select>
      </>
      <>
        <label htmlFor="trailerId">Trailer</label>
        <Select
          defaultValue=""
          name="trailerId"
          id="trailerId"
          disabled={isLumperIssueType}
        >
          <option value="">-- N/A --</option>
          <OptionList
            optionData={trailerOptions}
            attributeID="trailerId"
            attributeName="name"
          />
        </Select>
      </>
      <label htmlFor="note">Note</label>
      <TextArea id="note" name="note" rows={4} />
      <div>
        <Button
          type="submit"
          variant="good"
          isLoading={isCreating}
          disabled={isCreating}
        >
          Create Comchek
        </Button>
      </div>
    </form>
  );
}
